import { useState } from 'react';
import { addDays, isAfter, isValid, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { formatByLocale } from 'utils/date';
import { dateRangeAtom } from '../atoms';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useBulkApproveDutyLinesMutation } from '../queries';
import { StyledTile } from 'components/shared/Tile/Tile.styles';
import LoadingButton from '@sdb/ui/atoms/Button';

const BulkApproveDuties = () => {
  const { t } = useTranslation();
  const [dateRange] = useRecoilState(dateRangeAtom);

  const [withApprovalFlow, setWithApprovalFlow] = useState<boolean>(false);

  const { mutate: bulkApproveDutyLines, isLoading } =
    useBulkApproveDutyLinesMutation();

  const onClickApprove = async () => {
    if (!(dateRange[0] && dateRange[1])) {
      return;
    }

    await bulkApproveDutyLines({
      startDate: dateRange[0] as Date,
      endDate: startOfDay(addDays(dateRange[1] as Date, 1)),
      withApprovalFlow
    });
  };

  const isValidDateRange =
    dateRange[0] &&
    dateRange[1] &&
    isValid(dateRange[0]) &&
    isValid(dateRange[1]);

  const dateRangeMisMatchError = Boolean(
    dateRange[0] &&
      dateRange[1] &&
      isAfter(dateRange[0] as Date, dateRange[1] as Date)
  );

  return (
    <StyledTile
      title={t('adminPortal.realization.bulkApproval.name')}
      sx={{
        height: '100%',
        p: 2,

        opacity: isValidDateRange && !dateRangeMisMatchError ? 1 : 0.5
      }}
      data-testid="bulk-approve"
    >
      <Typography variant="body2" sx={{ fontFamily: 'SourceSansPro-Regular' }}>
        {t('adminPortal.realization.bulkApproval.description')}
      </Typography>
      <Box pt={2}>
        <FormControlLabel
          disableTypography
          sx={{ fontSize: '13px', fontFamily: 'SourceSansPro-Regular' }}
          control={
            <Checkbox
              checked={withApprovalFlow}
              onChange={(evt, checked) => setWithApprovalFlow(checked)}
            />
          }
          label={
            t(
              'adminPortal.realization.bulkApproval.withApprovalFlowLabel'
            ) as string
          }
        />
      </Box>
      <Box display="flex" gap={2} alignItems="center" pt={2}>
        <LoadingButton
          size="small"
          variant="tonal"
          disabled={isLoading || !isValidDateRange || dateRangeMisMatchError}
          onClick={onClickApprove}
          loading={isLoading}
        >
          {t('adminPortal.realization.approveDutyLines')}
        </LoadingButton>
        {isValidDateRange && !dateRangeMisMatchError && (
          <Typography>
            {`${formatByLocale(dateRange[0] as Date)} ${t(
              'until'
            )} ${formatByLocale(dateRange[1] as Date)}`}
          </Typography>
        )}
      </Box>
    </StyledTile>
  );
};

export default BulkApproveDuties;
